import { useState, useCallback } from "react";
import { useGraphQLFetch } from "./use-graphql-fetch";

export interface WarehouseStockValue {
  totalStockValue: number;
  totalStockItems: number;
  totalLocations: number;
  lastUpdated: string;
}

interface DashboardSummaryResponse {
  data?: {
    dashboard?: {
      dashboardSummary?: {
        totalStockValue: number;
        totalStockItems: number;
        totalLocations: number;
        lastUpdated: string;
      };
    };
  };
  errors?: Array<{ message: string }>;
}

const WAREHOUSE_STOCK_VALUE_QUERY = `
  query GetWarehouseStockValue($warehouseCode: String) {
    dashboard {
      dashboardSummary(warehouseCode: $warehouseCode) {
        totalStockValue
        totalStockItems
        totalLocations
        lastUpdated
      }
    }
  }
`;

export const useWarehouseStockValue = () => {
  const { fetchGraphQL } = useGraphQLFetch();
  const [stockValue, setStockValue] = useState<WarehouseStockValue | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchStockValue = useCallback(
    async (warehouseCode?: string): Promise<WarehouseStockValue | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const result = await fetchGraphQL<DashboardSummaryResponse["data"]>(
          WAREHOUSE_STOCK_VALUE_QUERY,
          { warehouseCode }
        );

        if (result.errors && result.errors.length > 0) {
          throw new Error(result.errors[0].message);
        }

        const summary = result.data?.dashboard?.dashboardSummary;
        if (summary) {
          const value: WarehouseStockValue = {
            totalStockValue: summary.totalStockValue,
            totalStockItems: summary.totalStockItems,
            totalLocations: summary.totalLocations,
            lastUpdated: summary.lastUpdated,
          };
          setStockValue(value);
          return value;
        }

        return null;
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch stock value";
        setError(message);
        console.error("Stock value fetch error:", err);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchGraphQL]
  );

  return {
    stockValue,
    isLoading,
    error,
    fetchStockValue,
  };
};
